import styled from '@emotion/styled';
import { Helmet } from 'react-helmet-async';

import InfoSections from '~/components/InfoSections';
import TypingTest from '~/components/Typing';

const TestWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: var(--space-xl) 0 var(--space-lg);
`;

/* ─── STRUCTURED DATA ──────────────────────────────────────────────────── */
const webAppSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebApplication',
  name: 'Cryotype',
  url: 'https://cryotype.app',
  description:
    'A free, precision online typing test. Measure your WPM, raw WPM, and accuracy with real-time feedback, difficulty levels, history tracking, and shareable results.',
  applicationCategory: 'UtilitiesApplication',
  operatingSystem: 'Any',
  browserRequirements: 'Requires JavaScript',
  offers: {
    '@type': 'Offer',
    price: '0',
    priceCurrency: 'USD',
  },
  featureList: [
    'Real-time WPM measurement',
    'Raw WPM tracking',
    'Character-level accuracy analysis',
    'Three difficulty levels: easy, medium, hard',
    'Four time modes: 15, 30, 60, 120 seconds',
    'WPM over time chart',
    'Test history saved locally',
    'Shareable results with PNG download',
  ],
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What is a good typing speed (WPM)?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'The average typing speed is around 40 WPM. A speed of 60–80 WPM is considered proficient. Speeds above 80 WPM are fast, and anything above 100 WPM places you in the top 10% of typists globally.',
      },
    },
    {
      '@type': 'Question',
      name: 'What is the difference between WPM and raw WPM?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'WPM (net WPM) counts only correctly typed characters — it penalises errors. Raw WPM counts every keystroke regardless of whether the character was correct. The gap between them reveals how much errors are costing you in speed.',
      },
    },
    {
      '@type': 'Question',
      name: 'How is WPM calculated in a typing test?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'WPM is calculated as: (correct characters ÷ 5) ÷ minutes elapsed. The number 5 represents the average word length in English. So 300 correct characters in 1 minute = 60 WPM.',
      },
    },
    {
      '@type': 'Question',
      name: 'How can I improve my typing speed?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Focus on accuracy first — errors slow you down more than hesitation. Learn proper touch typing with all 10 fingers on the home row. Practice 15 minutes daily without looking at the keyboard. Most people improve 15–25 WPM within a few weeks.',
      },
    },
    {
      '@type': 'Question',
      name: 'How long does it take to reach 100 WPM?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'For a beginner, reaching 100 WPM typically takes 6–12 months of consistent daily practice. If you are already at 60–70 WPM, reaching 100 WPM usually takes 2–4 months of focused training.',
      },
    },
    {
      '@type': 'Question',
      name: 'Is touch typing faster than hunt-and-peck?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. The average hunt-and-peck typist reaches 35–45 WPM. Trained touch typists average 50–80 WPM and can exceed 120 WPM because their fingers move from muscle memory without visual scanning.',
      },
    },
  ],
};

const howToSchema = {
  '@context': 'https://schema.org',
  '@type': 'HowTo',
  name: 'How to Improve Your Typing Speed',
  description: 'Five proven methods to increase your typing speed and accuracy.',
  step: [
    {
      '@type': 'HowToStep',
      position: 1,
      name: 'Prioritise accuracy over speed',
      text: 'Target 95%+ accuracy first. Every correction keystroke costs more time than a brief pause before typing correctly. Speed grows naturally once muscle memory is clean.',
    },
    {
      '@type': 'HowToStep',
      position: 2,
      name: 'Learn home-row touch typing',
      text: 'Keep left fingers on A-S-D-F and right fingers on J-K-L-;. All keys are reachable from this position without looking down.',
    },
    {
      '@type': 'HowToStep',
      position: 3,
      name: 'Never look at the keyboard',
      text: 'Cover your hands or use a blank keyboard to break the habit of looking down. Push through the first two weeks — it becomes automatic.',
    },
    {
      '@type': 'HowToStep',
      position: 4,
      name: 'Practice in short, consistent sessions',
      text: '15 minutes of focused daily practice beats two hours once a week. Use short time modes for high-intensity sprints and let muscle memory consolidate overnight.',
    },
    {
      '@type': 'HowToStep',
      position: 5,
      name: 'Track your progress and analyse errors',
      text: 'Use the history panel and WPM chart to spot patterns. Drops at the end of a test indicate stamina issues. Consistent low accuracy means specific key combinations need drilling.',
    },
  ],
};

/* ─── PAGE COMPONENT ────────────────────────────────────────────────────── */
export default function Home() {
  return (
    <>
      <Helmet>
        <title>Cryotype — Free Online Typing Test | Measure Your WPM &amp; Accuracy</title>
        <meta
          name="description"
          content="Take a free precision typing test and measure your WPM, raw WPM, and accuracy in real time. Three difficulty levels, 15–120 second modes, history tracking, and shareable results."
        />
        <meta
          name="keywords"
          content="typing test, WPM test, words per minute, typing speed test, online typing test, free typing test, improve typing speed, touch typing, typing accuracy, keyboard typing"
        />
        <link rel="canonical" href="https://cryotype.app/" />

        {/* JSON-LD */}
        <script type="application/ld+json">{JSON.stringify(webAppSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(howToSchema)}</script>
      </Helmet>

      <TestWrapper>
        <TypingTest />
      </TestWrapper>

      <InfoSections />
    </>
  );
}
